import {
  QueryClientProvider,
  QueryErrorResetBoundary,
} from "@tanstack/react-query";
import { useEffect, useState, lazy, Suspense } from "react";
// import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { Provider } from "react-redux";
import { registerServiceWorker } from "./utils/serviceWorker";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { SessionCacheReset } from "./components/auth/SessionCacheReset";
import { ThemeProvider } from "./contexts/ThemeContext";
import { RoleProvider } from "./contexts/RoleContext";
import { I18nProvider } from "./i18n/providers/I18nProvider";
import { store } from "./store";

// Layouts
import AuthLayout from "./layouts/AuthLayout";
import MainLayout from "./layouts/MainLayout";

// Lazy Components
import {
  LazyMobileDashboardPage,
  LazyCreateEventPage,
  LazyEventDetailsPage,
  LazyEventList,
  LazyEventEditPage,
  LazyEventCalendarPage,
  LazySupplierAnalyticsDashboard,
  LazyAnalyticsDashboard,
  LazyAnalyticsPage,
  LazyDashboardBuilderPage,
  LazyEventsAnalyticsPage,
  LazyParticipantsAnalyticsPage,
  LazyUsersAnalyticsPage,
  LazyConversionsAnalyticsPage,
  LazyDocumentsPage,
  LazyContractsPage,
  LazyDocumentInvoicesPage,
  LazyDocumentReportsPage,
  LazyTemplatesPage,
  LazyDocumentEditorPage,
  LazyMarketplacePage,
  LazyMarketplaceSearchPage,
  LazyMarketplaceComparePage,
  LazyMarketplaceFavoritesPage,
  LazySupplierDetailsPage,
  LazyServiceDetailsPage,
  LazyMarketplaceServicesPage,
  LazyOrderDetailPage,
  LazyChatPage,
  LazyVideoCallPage,
  LazyCommunityPage,
  LazyCollaborationPage,
  LazyReferralDashboardPage,
  LazyReferralEarningsPage,
  LazyReferralNetworkPage,
  LazyReferralLeaderboardPage,
  LazyReferralSettingsPage,
  LazyReferralInvitePage,
  LazyAgentPayoutsPage,
  LazySubscriptionPage,
  LazyFinancePage,
  LazyBudgetPlannerPage,
  LazyPaymentsPage,
  LazyReportsPage,
  LazyReportTemplatesPage,
  LazyInvoicesPage,
  LazyFinancialAnalyticsPage,
  LazyTransactionsPage,
  LazyCommissionsPage,
  LazyTeamPage,
  LazyNotificationsPage,
  LazyPartnersPage,
  LazyPersonalizationPage,
  LazySettingsPage,
  LazyNotificationSettingsPage,
  LazySecuritySettingsPage,
  LazyProfilePage,
  LazyPrivacySettingsPage,
  LazyTaxInformationPage,
  LazyCrmIntegrationsPage,
  LazyWebhooksSettingsPage,
  LazyApiKeysPage,
  LazySsoCallbackPage,
  LazySsoSettingsPage,
  LazyEventLeaderboardPage,
  LazyGamificationSettingsPage,
  LazyExhibitorDashboardPage,
  LazyLeadScannerPage,
  LazyLeadsListPage,
  LazyLeadKioskPage,
  LazyUsersListPage,
  LazyUserCreatePage,
  LazyUserDetailPage,
  LazyTasksPage, // Главная страница задач
  LazyTaskCreatePage,
  LazyEventTasksPage,
  LazyEventTaskCreatePage,
  LazyEventTaskDetailPage,
  LazyEventTaskEditPage,
  LazyEventPopularPage,
  LazyEventOverviewPage,
  LazyEventManagementPage,
  LazyEventParticipantsPage,
  LazyLiveEventPage,
  LazyEventAgendaPage,
  LazyEventBudgetPage,
  LazyOrdersPage,
  LazyEventQRPage,
  LazyEventAttendancePage,
  LazyEventResourcesPage,
  LazyEventGuestJoinPage,
  LazyMyEventQRPage,
  LazyPublicEventRegistrationPage,
  LazySurveyBuilderPage,
  LazySurveyRespondPage,
  LazySurveyResultsPage,
  LazyBadgeDesignerPage,
  LazyBadgePrintPage,
  LazyBadgeKioskPage,
  LazyMonitoringPage,
  LazyAdminDashboard,
  LazyAdminUsers,
  LazyAdminModeration,
  LazyAdminSettings,
  LazyAdminReports,
  LazyAdminSystemPage,
  LazyAdminSecurityPage,
  LazyAdminBackupPage,
  LazyAdminReferralsPage,
  LazyFilesPage,
  LazySupplierCatalogPage,
  LazySpeakerStatsPage,
  LazySupplierHubPage,
} from "./utils/lazy-utils";

// Core Components (не lazy - используются сразу)
import { AuthInitializer } from "./components/auth/AuthInitializer";
import LoginForm from "./components/auth/LoginForm";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import RegisterForm from "./components/auth/RegisterForm";
import NavigateKeepingQuery from "./components/navigation/NavigateKeepingQuery";
import VerifyEmailPage from "./pages/auth/VerifyEmailPage";
import RegistrationSuccessPage from "./pages/auth/RegistrationSuccessPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import Dashboard from "./pages/Dashboard";
import ErrorBoundary from "./components/system/ErrorBoundary";
import { PageLoading } from "./components/system/LoadingStates";
import { ForbiddenPage, NotFoundPage } from "./pages/errors";
import SupportPage from "./pages/support/SupportPage";
import { BookingSuccessPage } from "./pages/booking/BookingSuccessPage";
import { CookieConsent } from "./components/CookieConsent";
import { CardGateProvider } from "./components/subscription/CardGateProvider";
import { useDeepLinks } from "./hooks/useDeepLinks";
import {
  setupSessionTimeout,
  setupMultiTabDetection,
} from "./utils/session-security";
import { AdminLayout } from "./components/layout/AdminLayout";
import config from "./config/env";
import { InternalRole } from "./types/enums/internal-roles.enum";
import { EventRole } from "./types/enums/event-roles.enum";

// Role-specific компоненты (не lazy - используются сразу)
import OrganizerDashboard from "./components/roleSpecific/OrganizerDashboard";
import SupplierDashboard from "./components/roleSpecific/SupplierDashboard";
import ParticipantDashboard from "./components/roleSpecific/ParticipantDashboard";
import SpeakerDashboard from "./components/roleSpecific/SpeakerDashboard";
import AdminDashboard from "./components/roleSpecific/AdminDashboard";
import ManagerDashboard from "./components/roleSpecific/ManagerDashboard";
import EventAdminDashboard from "./components/roleSpecific/EventAdminDashboard";
import ContentModeratorDashboard from "./components/roleSpecific/ContentModeratorDashboard";
import SupportAgentDashboard from "./components/roleSpecific/SupportAgentDashboard";
import CoOrganizerDashboard from "./components/roleSpecific/CoOrganizerDashboard";
import GuestDashboard from "./components/roleSpecific/GuestDashboard";
import { RoleDashboardLayout } from "./components/dashboard/RoleDashboardLayout";

// Toast notifications
import { createAppQueryClient } from "./config/queryClient";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const LazyTermsOfServicePage = lazy(
  () => import("./pages/legal/TermsOfServicePage"),
);
const LazyPrivacyPolicyPage = lazy(
  () => import("./pages/legal/PrivacyPolicyPage"),
);
const LazySubProcessorsPage = lazy(
  () => import("./pages/legal/SubProcessorsPage"),
);

const DeepLinkHandler = () => {
  useDeepLinks();
  return null;
};

function App() {
  const [isInitializing, setIsInitializing] = useState(true);

  // Создаем клиент для React Query (PROMPT_10; настройка — `config/queryClient.ts`)
  const queryClient = createAppQueryClient();

  // Имитируем короткую задержку при загрузке, чтобы пользователь видел загрузку
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitializing(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Инициализация session security (только для авторизованных пользователей)
  useEffect(() => {
    let cleanup: (() => void) | undefined;
    import("./utils/secure-storage").then(({ SecureStorage }) =>
      SecureStorage.get("accessToken").then((accessToken) => {
        if (accessToken) {
          cleanup = setupSessionTimeout();
          setupMultiTabDetection();
        }
      }),
    );
    return () => cleanup?.();
  }, []);

  useEffect(() => {
    registerServiceWorker().then(() => {});
  }, []);

  if (isInitializing) {
    return (
      <ThemeProvider>
        <PageLoading message="Запуск приложения..." />
      </ThemeProvider>
    );
  }

  // Компонент для отображения ошибок
  // Кастомный ErrorBoundary уже содержит fallback UI

  return (
    <I18nProvider>
      <QueryErrorResetBoundary>
        {() => (
          <ErrorBoundary>
            <QueryClientProvider client={queryClient}>
              <Provider store={store}>
                <AuthInitializer />
                <ThemeProvider>
                  <AuthProvider>
                    {/* AD-608: смена пользователя в вкладке — сброс кэша запросов и Redux */}
                    <SessionCacheReset />
                    <RoleProvider>
                      <Router>
                        <DeepLinkHandler />
                        {/* ADR-TRIAL-card-gate (Фаза 5): глобальный оверлей card-gate —
                            402 TRIAL_CARD_REQUIRED → привязка карты → retry исходного запроса */}
                        <CardGateProvider />
                        <Routes>
                          {/* Public self-service registration via token/QR */}
                          <Route
                            path="/join/:token"
                            element={<LazyPublicEventRegistrationPage />}
                          />

                          {/* Public survey respond — no auth required */}
                          <Route
                            path="/surveys/:surveyToken/respond"
                            element={<LazySurveyRespondPage />}
                          />

                          {/* Survey results — protected */}
                          <Route
                            path="/surveys/:surveyId/results"
                            element={
                              <ProtectedRoute>
                                <LazySurveyResultsPage />
                              </ProtectedRoute>
                            }
                          />

                          {/* Badge kiosk — public, no auth */}
                          <Route
                            path="/kiosk/:eventId"
                            element={<LazyBadgeKioskPage />}
                          />

                          {/* Lead kiosk — public self-service QR scanner, no auth */}
                          <Route
                            path="/lead-kiosk/:kioskToken"
                            element={<LazyLeadKioskPage />}
                          />

                          {/* SSO callback — public, receives tokens from IdP */}
                          <Route
                            path="/sso/callback"
                            element={<LazySsoCallbackPage />}
                          />

                          {/* Exhibitor lead retrieval */}
                          <Route
                            path="/exhibitor"
                            element={
                              <ProtectedRoute>
                                <LazyExhibitorDashboardPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/exhibitor/:boothId/scan"
                            element={
                              <ProtectedRoute>
                                <LazyLeadScannerPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/exhibitor/:boothId/leads"
                            element={
                              <ProtectedRoute>
                                <LazyLeadsListPage />
                              </ProtectedRoute>
                            }
                          />

                          {/* Badge results — protected */}
                          <Route
                            path="/badges/:eventId/print"
                            element={
                              <ProtectedRoute>
                                <LazyBadgePrintPage />
                              </ProtectedRoute>
                            }
                          />

                          {/* Public event guest join — no auth required */}
                          <Route
                            path="/events/:eventId/join"
                            element={<LazyEventGuestJoinPage />}
                          />

                          {/*
                            AD-460. «Мой QR» — код участника, показанный самому
                            участнику. Под ProtectedRoute: сервер выдаёт код
                            только своему владельцу или организатору, и берёт он
                            спрашивающего из токена — без входа спрашивать нечем.
                          */}
                          <Route
                            path="/events/:eventId/my-qr"
                            element={
                              <ProtectedRoute>
                                <LazyMyEventQRPage />
                              </ProtectedRoute>
                            }
                          />

                          {/* Auth-required invite link — requires login or registration */}
                          <Route
                            path="/events/:eventId/invite"
                            element={
                              <ProtectedRoute>
                                <LazyEventGuestJoinPage />
                              </ProtectedRoute>
                            }
                          />

                          {/* Публичные юридические страницы — без авторизации */}
                          <Route
                            path="/terms-of-service"
                            element={
                              <Suspense
                                fallback={<PageLoading message="Loading..." />}
                              >
                                <LazyTermsOfServicePage />
                              </Suspense>
                            }
                          />
                          <Route
                            path="/privacy-policy"
                            element={
                              <Suspense
                                fallback={<PageLoading message="Loading..." />}
                              >
                                <LazyPrivacyPolicyPage />
                              </Suspense>
                            }
                          />
                          <Route
                            path="/sub-processors"
                            element={
                              <Suspense
                                fallback={<PageLoading message="Loading..." />}
                              >
                                <LazySubProcessorsPage />
                              </Suspense>
                            }
                          />

                          {/* Маршруты авторизации */}
                          <Route path="/auth" element={<AuthLayout />}>
                            <Route
                              index
                              element={<Navigate to="/auth/login" replace />}
                            />
                            <Route path="login" element={<LoginForm />} />
                            <Route path="register" element={<RegisterForm />} />
                            <Route
                              path="registration-success"
                              element={<RegistrationSuccessPage />}
                            />
                            <Route
                              path="verify-email"
                              element={<VerifyEmailPage />}
                            />
                            <Route
                              path="forgot-password"
                              element={<ForgotPasswordPage />}
                            />
                            <Route
                              path="reset-password"
                              element={<ResetPasswordPage />}
                            />
                          </Route>

                          {/* Защищенные маршруты */}
                          <Route
                            path="/"
                            element={
                              <ProtectedRoute>
                                <MainLayout />
                              </ProtectedRoute>
                            }
                          >
                            <Route
                              index
                              element={<Navigate to="/dashboard" replace />}
                            />

                            {/* Дашборд */}
                            <Route
                              path="dashboard"
                              element={
                                <ProtectedRoute>
                                  <Dashboard />
                                </ProtectedRoute>
                              }
                            />

                            {/* Конструктор дашборда */}
                            <Route
                              path="dashboard/builder"
                              element={
                                <ProtectedRoute>
                                  <LazyDashboardBuilderPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* Role-specific дашборд */}
                            <Route
                              path="dashboard/role"
                              element={
                                <ProtectedRoute>
                                  <RoleDashboardLayout />
                                </ProtectedRoute>
                              }
                            />

                            {/* Продвинутые виджеты */}
                            <Route
                              path="advanced-widgets"
                              element={
                                <ProtectedRoute>
                                  <Dashboard />
                                </ProtectedRoute>
                              }
                            />

                            {/* Персонализация */}
                            <Route
                              path="personalization"
                              element={
                                <ProtectedRoute>
                                  <LazyPersonalizationPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* Коллаборация */}
                            <Route
                              path="collaboration"
                              element={
                                <ProtectedRoute>
                                  <LazyCollaborationPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* Мобильная версия */}
                            <Route
                              path="mobile"
                              element={
                                <ProtectedRoute>
                                  <LazyMobileDashboardPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* Настройки */}
                            <Route path="settings">
                              <Route
                                index
                                element={
                                  <ProtectedRoute>
                                    <LazySettingsPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="notifications"
                                element={
                                  <ProtectedRoute>
                                    <LazyNotificationSettingsPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="security"
                                element={
                                  <ProtectedRoute>
                                    <LazySecuritySettingsPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="profile"
                                element={
                                  <ProtectedRoute>
                                    <LazyProfilePage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="privacy"
                                element={
                                  <ProtectedRoute>
                                    <LazyPrivacySettingsPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="tax"
                                element={
                                  <ProtectedRoute>
                                    <LazyTaxInformationPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="subscription"
                                element={
                                  <ProtectedRoute>
                                    <LazySubscriptionPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="crm"
                                element={
                                  <ProtectedRoute>
                                    <LazyCrmIntegrationsPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="webhooks"
                                element={
                                  <ProtectedRoute>
                                    <LazyWebhooksSettingsPage />
                                  </ProtectedRoute>
                                }
                              />
                              {/*
                                AD-468. Маршрут стоял без ролей, и любой вошедший,
                                набравший адрес (или вернувшийся сюда после входа по
                                `state.from`), получал экран, который сервер ему не
                                откроет: все шесть методов `auth/sso` — под
                                @InternalRoles(SUPER_ADMIN, PLATFORM_ADMIN). Роли здесь
                                те же, что у пункта меню (AD-416).
                              */}
                              <Route
                                path="sso"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      InternalRole.PLATFORM_ADMIN,
                                      InternalRole.SUPER_ADMIN,
                                    ]}
                                  >
                                    <LazySsoSettingsPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="api-keys"
                                element={
                                  <ProtectedRoute>
                                    <LazyApiKeysPage />
                                  </ProtectedRoute>
                                }
                              />
                            </Route>

                            {/* Для пользователей (требуются права администратора) */}
                            <Route path="users">
                              <Route
                                index
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      InternalRole.SUPER_ADMIN,
                                      InternalRole.PLATFORM_ADMIN,
                                    ]}
                                  >
                                    <LazyUsersListPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="create"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      InternalRole.SUPER_ADMIN,
                                      InternalRole.PLATFORM_ADMIN,
                                    ]}
                                  >
                                    <LazyUserCreatePage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path=":userId"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      InternalRole.SUPER_ADMIN,
                                      InternalRole.PLATFORM_ADMIN,
                                    ]}
                                  >
                                    <LazyUserDetailPage />
                                  </ProtectedRoute>
                                }
                              />
                            </Route>

                            {/* LEGACY ADMIN ROUTES - MOVED TO /admin LAYOUT (lines 1020-1034) */}
                            {/* These routes are duplicates and should be accessed via /admin/* instead */}

                            {/* Для задач */}
                            <Route
                              path="tasks"
                              element={
                                <ProtectedRoute>
                                  <LazyTasksPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="tasks/create"
                              element={
                                <ProtectedRoute>
                                  <LazyTaskCreatePage />
                                </ProtectedRoute>
                              }
                            />

                            {/* Для мероприятий */}
                            <Route path="events">
                              <Route
                                index
                                element={
                                  <ProtectedRoute>
                                    <LazyEventList />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="create"
                                element={
                                  <ProtectedRoute>
                                    <LazyCreateEventPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="calendar"
                                element={
                                  <ProtectedRoute>
                                    <LazyEventCalendarPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="popular"
                                element={
                                  <ProtectedRoute>
                                    <LazyEventPopularPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="overview"
                                element={
                                  <ProtectedRoute>
                                    <LazyEventOverviewPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path=":eventId"
                                element={
                                  <ProtectedRoute>
                                    <LazyEventDetailsPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path=":eventId/edit"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      EventRole.ORGANIZER,
                                      EventRole.CO_ORGANIZER,
                                      EventRole.EVENT_ADMIN,
                                    ]}
                                  >
                                    <LazyEventEditPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path=":eventId/management"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      EventRole.ORGANIZER,
                                      EventRole.CO_ORGANIZER,
                                      EventRole.EVENT_ADMIN,
                                    ]}
                                  >
                                    <LazyEventManagementPage />
                                  </ProtectedRoute>
                                }
                              />

                              <Route
                                path=":eventId/participants"
                                element={
                                  <ProtectedRoute>
                                    <LazyEventParticipantsPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path=":eventId/live"
                                element={
                                  <ProtectedRoute>
                                    <LazyLiveEventPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path=":eventId/qr"
                                element={
                                  <ProtectedRoute>
                                    <LazyEventQRPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path=":eventId/attendance"
                                element={
                                  <ProtectedRoute>
                                    <LazyEventAttendancePage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path=":eventId/resources"
                                element={
                                  <ProtectedRoute>
                                    <LazyEventResourcesPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path=":eventId/agenda"
                                element={
                                  <ProtectedRoute>
                                    <LazyEventAgendaPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path=":eventId/budget"
                                element={
                                  <ProtectedRoute>
                                    <LazyEventBudgetPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path=":eventId/surveys"
                                element={
                                  <ProtectedRoute>
                                    <LazySurveyBuilderPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path=":eventId/badges"
                                element={
                                  <ProtectedRoute>
                                    <LazyBadgePrintPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path=":eventId/badges/designer"
                                element={
                                  <ProtectedRoute>
                                    <LazyBadgeDesignerPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path=":eventId/badges/designer/:templateId"
                                element={
                                  <ProtectedRoute>
                                    <LazyBadgeDesignerPage />
                                  </ProtectedRoute>
                                }
                              />

                              {/* Задачи мероприятия */}
                              <Route path=":eventId/tasks">
                                <Route
                                  index
                                  element={
                                    <ProtectedRoute>
                                      <LazyEventTasksPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="create"
                                  element={
                                    <ProtectedRoute>
                                      <LazyEventTaskCreatePage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path=":taskId"
                                  element={
                                    <ProtectedRoute>
                                      <LazyEventTaskDetailPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path=":taskId/edit"
                                  element={
                                    <ProtectedRoute>
                                      <LazyEventTaskEditPage />
                                    </ProtectedRoute>
                                  }
                                />
                              </Route>
                            </Route>

                            {/* Gamification */}
                            <Route
                              path=":eventId/leaderboard"
                              element={<LazyEventLeaderboardPage />}
                            />
                            <Route
                              path=":eventId/gamification"
                              element={
                                <ProtectedRoute>
                                  <LazyGamificationSettingsPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* Успех оплаты (redirect после 3DS) */}
                            <Route
                              path="booking-success"
                              element={
                                <ProtectedRoute>
                                  <BookingSuccessPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* Маркетплейс */}
                            <Route
                              path="marketplace"
                              element={
                                <ErrorBoundary>
                                  <ProtectedRoute>
                                    <LazyMarketplacePage />
                                  </ProtectedRoute>
                                </ErrorBoundary>
                              }
                            />
                            <Route
                              path="marketplace/search"
                              element={
                                <ErrorBoundary>
                                  <ProtectedRoute>
                                    <LazyMarketplaceSearchPage />
                                  </ProtectedRoute>
                                </ErrorBoundary>
                              }
                            />
                            <Route
                              path="marketplace/compare"
                              element={
                                <ErrorBoundary>
                                  <ProtectedRoute>
                                    <LazyMarketplaceComparePage />
                                  </ProtectedRoute>
                                </ErrorBoundary>
                              }
                            />
                            <Route
                              path="marketplace/favorites"
                              element={
                                <ErrorBoundary>
                                  <ProtectedRoute>
                                    <LazyMarketplaceFavoritesPage />
                                  </ProtectedRoute>
                                </ErrorBoundary>
                              }
                            />
                            <Route
                              path="marketplace/orders"
                              element={
                                <ErrorBoundary>
                                  <ProtectedRoute>
                                    <LazyOrdersPage />
                                  </ProtectedRoute>
                                </ErrorBoundary>
                              }
                            />
                            <Route
                              path="marketplace/orders/:id"
                              element={
                                <ProtectedRoute>
                                  <LazyOrderDetailPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="marketplace/suppliers/:id"
                              element={
                                <ProtectedRoute>
                                  <LazySupplierDetailsPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="marketplace/services/:id"
                              element={
                                <ProtectedRoute>
                                  <LazyServiceDetailsPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="marketplace/services"
                              element={
                                <ErrorBoundary>
                                  <ProtectedRoute>
                                    <LazyMarketplaceServicesPage />
                                  </ProtectedRoute>
                                </ErrorBoundary>
                              }
                            />
                            <Route
                              path="marketplace/supplier-hub"
                              element={
                                <ProtectedRoute>
                                  <LazySupplierHubPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* Заказы */}
                            <Route
                              path="orders"
                              element={
                                <ProtectedRoute>
                                  <LazyOrdersPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* Услуги (для участников и пользователей) */}
                            <Route
                              path="services"
                              element={
                                <ErrorBoundary>
                                  <ProtectedRoute>
                                    <LazyMarketplacePage />
                                  </ProtectedRoute>
                                </ErrorBoundary>
                              }
                            />
                            <Route
                              path="services/recommended"
                              element={
                                <ErrorBoundary>
                                  <ProtectedRoute>
                                    <LazyMarketplacePage />
                                  </ProtectedRoute>
                                </ErrorBoundary>
                              }
                            />
                            <Route
                              path="services/reviews"
                              element={
                                <ErrorBoundary>
                                  <ProtectedRoute>
                                    <LazyMarketplacePage />
                                  </ProtectedRoute>
                                </ErrorBoundary>
                              }
                            />

                            {/* 🎯 Role-specific страницы */}
                            <Route
                              path="organizer"
                              element={
                                <ProtectedRoute>
                                  <OrganizerDashboard />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="supplier"
                              element={
                                <ProtectedRoute>
                                  <SupplierDashboard />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="participant"
                              element={
                                <ProtectedRoute>
                                  <ParticipantDashboard />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="speaker"
                              element={
                                <ProtectedRoute>
                                  <SpeakerDashboard />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="admin"
                              element={
                                <ProtectedRoute>
                                  <AdminDashboard />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="manager"
                              element={
                                <ProtectedRoute>
                                  <ManagerDashboard />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="event-admin"
                              element={
                                <ProtectedRoute>
                                  <EventAdminDashboard />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="content-moderator"
                              element={
                                <ProtectedRoute>
                                  <ContentModeratorDashboard />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="support-agent"
                              element={
                                <ProtectedRoute>
                                  <SupportAgentDashboard />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="co-organizer"
                              element={
                                <ProtectedRoute>
                                  <CoOrganizerDashboard />
                                </ProtectedRoute>
                              }
                            />
                            <Route path="guest" element={<GuestDashboard />} />

                            {/* Supplier Analytics Dashboard */}
                            <Route
                              path="supplier-analytics"
                              element={
                                <ProtectedRoute>
                                  <LazySupplierAnalyticsDashboard />
                                </ProtectedRoute>
                              }
                            />

                            {/* Аналитика */}
                            <Route path="analytics">
                              {/* Главная страница аналитики — доступна всем авторизованным (ролевая логика внутри) */}
                              <Route
                                index
                                element={
                                  <ProtectedRoute>
                                    <LazyAnalyticsPage />
                                  </ProtectedRoute>
                                }
                              />
                              {/* Платформенный дашборд — только admin/manager */}
                              <Route
                                path="dashboard"
                                element={
                                  <ProtectedRoute
                                    requiredPermissions={["analytics.view"]}
                                  >
                                    <LazyAnalyticsDashboard />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="builder"
                                element={
                                  <ProtectedRoute
                                    requiredPermissions={["analytics.view"]}
                                  >
                                    <LazyDashboardBuilderPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="events"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      EventRole.ORGANIZER,
                                      EventRole.CO_ORGANIZER,
                                      EventRole.EVENT_ADMIN,
                                      InternalRole.PLATFORM_MANAGER,
                                      InternalRole.PLATFORM_ADMIN,
                                      InternalRole.SUPER_ADMIN,
                                    ]}
                                  >
                                    <LazyEventsAnalyticsPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="participants"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      EventRole.ORGANIZER,
                                      EventRole.CO_ORGANIZER,
                                      EventRole.EVENT_ADMIN,
                                      InternalRole.PLATFORM_MANAGER,
                                      InternalRole.PLATFORM_ADMIN,
                                      InternalRole.SUPER_ADMIN,
                                    ]}
                                  >
                                    <LazyParticipantsAnalyticsPage />
                                  </ProtectedRoute>
                                }
                              />
                              {/* Аналитика пользователей — только admin */}
                              <Route
                                path="users"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      InternalRole.SUPER_ADMIN,
                                      InternalRole.PLATFORM_ADMIN,
                                    ]}
                                  >
                                    <LazyUsersAnalyticsPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="conversions"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      EventRole.ORGANIZER,
                                      EventRole.CO_ORGANIZER,
                                      EventRole.EVENT_ADMIN,
                                      InternalRole.PLATFORM_MANAGER,
                                      InternalRole.PLATFORM_ADMIN,
                                      InternalRole.SUPER_ADMIN,
                                    ]}
                                  >
                                    <LazyConversionsAnalyticsPage />
                                  </ProtectedRoute>
                                }
                              />
                            </Route>

                            {/* Редиректы со старых/наследованных путей на актуальные */}
                            <Route
                              path="calendar"
                              element={
                                <Navigate to="/events/calendar" replace />
                              }
                            />
                            <Route
                              path="marketplace/quotation"
                              element={<Navigate to="/marketplace" replace />}
                            />
                            <Route
                              path="community/ask"
                              element={<Navigate to="/community" replace />}
                            />
                            <Route
                              path="community/experts"
                              element={<Navigate to="/community" replace />}
                            />

                            {/* === SUPPLIER === */}
                            <Route
                              path="supplier/orders"
                              element={
                                <ProtectedRoute>
                                  <LazyOrdersPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="catalog"
                              element={
                                <ProtectedRoute>
                                  <LazySupplierCatalogPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="catalog/add"
                              element={
                                <ProtectedRoute>
                                  <LazySupplierCatalogPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* === NETWORKING (PARTICIPANT / SPEAKER) === */}
                            <Route
                              path="network"
                              element={
                                <ProtectedRoute>
                                  <LazyCommunityPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="network/contacts"
                              element={
                                <ProtectedRoute>
                                  <LazyCommunityPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="network/groups"
                              element={
                                <ProtectedRoute>
                                  <LazyCommunityPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* === SUPPLIER COMMUNITY === */}
                            <Route
                              path="clients"
                              element={
                                <ProtectedRoute>
                                  <LazyCommunityPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="reviews"
                              element={
                                <ProtectedRoute>
                                  <LazyMarketplaceServicesPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* === FINANCE — ролевые подстраницы === */}
                            {/* AD-612: «Доходы» — поступления вызывающему (direction=incoming). */}
                            <Route
                              path="finance/income"
                              element={
                                <ProtectedRoute>
                                  <LazyTransactionsPage variant="income" />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="finance/taxes"
                              element={
                                <ProtectedRoute>
                                  <LazyTransactionsPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="finance/fees"
                              element={
                                <ProtectedRoute>
                                  <LazyTransactionsPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="finance/payouts"
                              element={
                                <ProtectedRoute>
                                  <LazyPaymentsPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="finance/contracts"
                              element={
                                <ProtectedRoute>
                                  <LazyContractsPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* === SPEAKER STATS === */}
                            <Route
                              path="speaker-stats"
                              element={
                                <ProtectedRoute>
                                  <LazySpeakerStatsPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="speaker-stats/presentations"
                              element={
                                <ProtectedRoute>
                                  <LazySpeakerStatsPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="speaker-stats/feedback"
                              element={
                                <ProtectedRoute>
                                  <LazySpeakerStatsPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="speaker-stats/ratings"
                              element={
                                <ProtectedRoute>
                                  <LazySpeakerStatsPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* === SUPPLIER ANALYTICS === */}
                            <Route
                              path="analytics/sales"
                              element={
                                <ProtectedRoute>
                                  <LazySupplierAnalyticsDashboard />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="analytics/clients"
                              element={
                                <ProtectedRoute>
                                  <LazySupplierAnalyticsDashboard />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="analytics/services"
                              element={
                                <ProtectedRoute>
                                  <LazySupplierAnalyticsDashboard />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="analytics/ratings"
                              element={
                                <ProtectedRoute>
                                  <LazySupplierAnalyticsDashboard />
                                </ProtectedRoute>
                              }
                            />

                            {/* Финансы */}
                            <Route path="finance">
                              <Route
                                index
                                element={
                                  <ProtectedRoute>
                                    <LazyFinancePage />
                                  </ProtectedRoute>
                                }
                              />
                              {/*
                                AD-467. Событийные роли сняты: `finance/budget` на
                                сервере целиком под платформенными ролями, и открыть
                                его организатору нельзя не «ослабив гуард», а только
                                заведя владение — чтения бюджета идут БЕЗ фильтра по
                                владельцу (BudgetController, комментарий XS-001), то
                                есть организатор увидел бы бюджеты всех. У бюджета
                                события своё место: /events/:eventId/budget.
                              */}
                              <Route
                                path="budget"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      InternalRole.PLATFORM_MANAGER,
                                      InternalRole.PLATFORM_ADMIN,
                                      InternalRole.SUPER_ADMIN,
                                    ]}
                                  >
                                    <LazyBudgetPlannerPage />
                                  </ProtectedRoute>
                                }
                              />
                              {/*
                                AD-467 убрал отсюда событийные роли: список платежей и
                                сводка были платформенными, и экран открывался отказом.
                                AD-604 DoD 4 (решения Сергея 01.10): сервер отдаёт
                                каждому его собственную область — платформе (PAYMENT_READ_ALL)
                                всё, остальным платежи их транзакций и те, где они названы
                                поставщиком. Поставщику пункт «Платежи» в меню стоит —
                                маршрут ему открыт. OPERATIONS_MANAGER читает всё
                                (PAYMENT_READ_ALL). Организатору пункт не возвращён: его
                                область — платежи его транзакций, а не всего события.
                              */}
                              <Route
                                path="payments"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      InternalRole.PLATFORM_MANAGER,
                                      InternalRole.OPERATIONS_MANAGER,
                                      InternalRole.PLATFORM_ADMIN,
                                      InternalRole.SUPER_ADMIN,
                                      EventRole.SUPPLIER,
                                    ]}
                                  >
                                    <LazyPaymentsPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="reports"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      EventRole.ORGANIZER,
                                      EventRole.CO_ORGANIZER,
                                      EventRole.EVENT_ADMIN,
                                      InternalRole.PLATFORM_MANAGER,
                                      InternalRole.PLATFORM_ADMIN,
                                      InternalRole.SUPER_ADMIN,
                                    ]}
                                  >
                                    <LazyReportsPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="templates"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      EventRole.ORGANIZER,
                                      EventRole.CO_ORGANIZER,
                                      EventRole.EVENT_ADMIN,
                                      InternalRole.PLATFORM_MANAGER,
                                      InternalRole.PLATFORM_ADMIN,
                                      InternalRole.SUPER_ADMIN,
                                    ]}
                                  >
                                    <LazyReportTemplatesPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="invoices"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      EventRole.ORGANIZER,
                                      EventRole.CO_ORGANIZER,
                                      EventRole.EVENT_ADMIN,
                                      InternalRole.PLATFORM_MANAGER,
                                      InternalRole.PLATFORM_ADMIN,
                                      InternalRole.SUPER_ADMIN,
                                    ]}
                                  >
                                    <LazyInvoicesPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="analytics"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      InternalRole.SUPER_ADMIN,
                                      InternalRole.PLATFORM_ADMIN,
                                    ]}
                                  >
                                    <LazyFinancialAnalyticsPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="transactions"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      InternalRole.SUPER_ADMIN,
                                      InternalRole.PLATFORM_ADMIN,
                                    ]}
                                  >
                                    <LazyTransactionsPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="commissions"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      InternalRole.SUPER_ADMIN,
                                      InternalRole.PLATFORM_ADMIN,
                                    ]}
                                  >
                                    <LazyCommissionsPage />
                                  </ProtectedRoute>
                                }
                              />
                            </Route>

                            {/* Документы */}
                            <Route path="documents">
                              <Route
                                index
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      EventRole.ORGANIZER,
                                      EventRole.CO_ORGANIZER,
                                      EventRole.EVENT_ADMIN,
                                      EventRole.SUPPLIER,
                                      InternalRole.PLATFORM_ADMIN,
                                      InternalRole.SUPER_ADMIN,
                                    ]}
                                  >
                                    <LazyDocumentsPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="contracts"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      EventRole.ORGANIZER,
                                      EventRole.CO_ORGANIZER,
                                      EventRole.EVENT_ADMIN,
                                      EventRole.SUPPLIER,
                                      InternalRole.PLATFORM_ADMIN,
                                      InternalRole.SUPER_ADMIN,
                                    ]}
                                  >
                                    <LazyContractsPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="invoices"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      EventRole.ORGANIZER,
                                      EventRole.CO_ORGANIZER,
                                      EventRole.EVENT_ADMIN,
                                      EventRole.SUPPLIER,
                                      InternalRole.PLATFORM_ADMIN,
                                      InternalRole.SUPER_ADMIN,
                                    ]}
                                  >
                                    <LazyDocumentInvoicesPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="reports"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      EventRole.ORGANIZER,
                                      EventRole.CO_ORGANIZER,
                                      EventRole.EVENT_ADMIN,
                                      EventRole.SUPPLIER,
                                      InternalRole.PLATFORM_ADMIN,
                                      InternalRole.SUPER_ADMIN,
                                    ]}
                                  >
                                    <LazyDocumentReportsPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="templates"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      EventRole.ORGANIZER,
                                      EventRole.CO_ORGANIZER,
                                      EventRole.EVENT_ADMIN,
                                      EventRole.SUPPLIER,
                                      InternalRole.PLATFORM_ADMIN,
                                      InternalRole.SUPER_ADMIN,
                                    ]}
                                  >
                                    <LazyTemplatesPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="editor"
                                element={
                                  <ProtectedRoute
                                    requiredRoles={[
                                      EventRole.ORGANIZER,
                                      EventRole.CO_ORGANIZER,
                                      EventRole.EVENT_ADMIN,
                                      EventRole.SUPPLIER,
                                      InternalRole.PLATFORM_ADMIN,
                                      InternalRole.SUPER_ADMIN,
                                    ]}
                                  >
                                    <LazyDocumentEditorPage />
                                  </ProtectedRoute>
                                }
                              />
                            </Route>

                            {/* Файлы */}
                            <Route
                              path="files"
                              element={
                                <ProtectedRoute>
                                  <LazyFilesPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* Сообщество */}
                            <Route
                              path="community"
                              element={
                                <ProtectedRoute>
                                  <LazyCommunityPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="chats"
                              element={
                                <ProtectedRoute>
                                  <LazyChatPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="chats/event/:eventId"
                              element={
                                <ProtectedRoute>
                                  <LazyChatPage />
                                </ProtectedRoute>
                              }
                            />
                            {/* Monitoring */}
                            <Route
                              path="monitoring"
                              element={
                                <ProtectedRoute
                                  requiredRoles={[
                                    InternalRole.SUPER_ADMIN,
                                    InternalRole.PLATFORM_ADMIN,
                                    InternalRole.MONITORING_ENGINEER,
                                    InternalRole.TECHNICAL_SUPPORT,
                                  ]}
                                >
                                  <LazyMonitoringPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* Support */}
                            <Route
                              path="support"
                              element={
                                <ProtectedRoute>
                                  <SupportPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="video-calls"
                              element={
                                <ProtectedRoute>
                                  <LazyVideoCallPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="team"
                              element={
                                <ProtectedRoute>
                                  <LazyTeamPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="notifications"
                              element={
                                <ProtectedRoute>
                                  <LazyNotificationsPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="partners"
                              element={
                                <ProtectedRoute>
                                  <LazyPartnersPage />
                                </ProtectedRoute>
                              }
                            />
                            {/* Redirects for legacy/alternative subscription URLs → canonical /settings/subscription */}
                            <Route
                              path="subscription"
                              element={
                                <Navigate to="/settings/subscription" replace />
                              }
                            />
                            <Route
                              path="pricing"
                              element={
                                <Navigate to="/settings/subscription" replace />
                              }
                            />
                            <Route
                              path="ai-packages"
                              element={
                                <Navigate to="/settings/subscription" replace />
                              }
                            />
                            <Route
                              path="subscription/plans"
                              element={
                                <Navigate to="/settings/subscription" replace />
                              }
                            />
                            <Route
                              path="account/subscription"
                              element={
                                <Navigate to="/settings/subscription" replace />
                              }
                            />
                            <Route
                              path="referral"
                              element={
                                <ProtectedRoute>
                                  <LazyReferralDashboardPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="referral/earnings"
                              element={
                                <ProtectedRoute>
                                  <LazyReferralEarningsPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="referral/network"
                              element={
                                <ProtectedRoute>
                                  <LazyReferralNetworkPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="referral/leaderboard"
                              element={
                                <ProtectedRoute>
                                  <LazyReferralLeaderboardPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="referral/settings"
                              element={
                                <ProtectedRoute>
                                  <LazyReferralSettingsPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="referral/invite"
                              element={
                                <ProtectedRoute>
                                  <LazyReferralInvitePage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="referral/payouts"
                              element={
                                <ProtectedRoute>
                                  <LazyAgentPayoutsPage />
                                </ProtectedRoute>
                              }
                            />
                          </Route>

                          {/* Перенаправление для входа */}
                          <Route
                            path="/login"
                            element={<Navigate to="/auth/login" replace />}
                          />
                          {/* AD-305: реферальная ссылка — `/register?ref=<код>`; код не должен
                              теряться на редиректе. */}
                          <Route
                            path="/register"
                            element={
                              <NavigateKeepingQuery to="/auth/register" />
                            }
                          />
                          {/* ResetPasswordPage ссылается на /forgot-password (без /auth) —
                              редирект по образцу /login и /register, чтобы ссылка не была мёртвой. */}
                          <Route
                            path="/forgot-password"
                            element={
                              <Navigate to="/auth/forgot-password" replace />
                            }
                          />

                          {/* Admin Panel Routes */}
                          <Route
                            path="/admin"
                            element={
                              <ProtectedRoute
                                requiredPermissions={["system.admin"]}
                              >
                                <AdminLayout />
                              </ProtectedRoute>
                            }
                          >
                            <Route index element={<LazyAdminDashboard />} />
                            <Route
                              path="dashboard"
                              element={<LazyAdminDashboard />}
                            />
                            <Route path="events" element={<LazyEventList />} />
                            <Route path="users" element={<LazyAdminUsers />} />
                            <Route
                              path="moderation"
                              element={<LazyAdminModeration />}
                            />
                            <Route
                              path="settings"
                              element={<LazyAdminSettings />}
                            />
                            <Route
                              path="reports"
                              element={<LazyAdminReports />}
                            />
                            <Route
                              path="system"
                              element={<LazyAdminSystemPage />}
                            />
                            <Route
                              path="security"
                              element={<LazyAdminSecurityPage />}
                            />
                            <Route
                              path="backup"
                              element={<LazyAdminBackupPage />}
                            />
                            <Route
                              path="referrals"
                              element={<LazyAdminReferralsPage />}
                            />
                          </Route>

                          {/* Страница 403 - Доступ запрещен */}
                          <Route
                            path="/forbidden"
                            element={<ForbiddenPage />}
                          />

                          {/* Dev-only роуты: диагностика и тестирование (не попадают в прод-бандл) */}
                          {config.IS_DEVELOPMENT && (
                            <>
                              <Route
                                path="/dev/routes"
                                element={(() => {
                                  // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
                                  const TestRoutesPage =
                                    require("./pages/test/TestRoutesPage").default;
                                  return <TestRoutesPage />;
                                })()}
                              />
                              <Route
                                path="/dev/tests"
                                element={(() => {
                                  // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
                                  const AdminTestsPage =
                                    require("./pages/admin/AdminTestsPage").default;
                                  return <AdminTestsPage />;
                                })()}
                              />
                            </>
                          )}

                          {/* Страница 404 */}
                          <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                      </Router>
                      {/* GDPR Cookie Consent Banner */}
                      <CookieConsent />
                    </RoleProvider>
                  </AuthProvider>
                </ThemeProvider>
              </Provider>
            </QueryClientProvider>
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </I18nProvider>
  );
}

export default App;
